import { useCallback, useEffect, useMemo, useState } from 'react'
import { isSameDay } from '@/calendar/calendarDateUtils'


/**
 * Re-renders when the local calendar day rolls over (midnight) and when the tab
 * becomes visible again, so “Today” / “Yesterday” stay accurate for long-lived sessions.
 *
 * @returns {Date} “Current moment” snapshot; prefer passing this as `options.now` to {@link formatConversationDayHeading}.
 */

export function useCalendarDayNow() {
  const [dayEpoch, setDayEpoch] = useState(0)
  const bump = useCallback(() => setDayEpoch((n) => n + 1), [])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    let timeoutId = /** @type {ReturnType<typeof setTimeout> | undefined} */ (undefined)

    const scheduleNextMidnight = () => {
      const now = new Date()
      const next = new Date(now)
      next.setDate(now.getDate() + 1)
      next.setHours(0, 0, 0, 0)
      const ms = Math.max(0, next.getTime() - now.getTime())
      timeoutId = setTimeout(() => {
        bump()
        scheduleNextMidnight()
      }, ms + 1)
    }

    scheduleNextMidnight()

    const onVisibility = () => {
      if (document.visibilityState === 'visible') bump()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      if (timeoutId !== undefined) clearTimeout(timeoutId)
    }
  }, [bump])

  return useMemo(() => new Date(), [dayEpoch])
}

/**
 * @param {number | Date} a
 * @param {number | Date} b
 * @returns {boolean}
 */
